import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    useStyleConfig,
    Radio,
    Stack,
    AspectRatio,
    Box,
    Button,
    Skeleton,
    VisuallyHidden,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import DisplayPrice from '../display-price'
import OmnibusPrice from '../display-price/omnibus-price'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronRightIcon} from '../icons'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import useSpecifications, {useArrayOfSpecifications} from '../../utils/specifications-utils'
import DynamicImage from '../dynamic-image'
import TechnicalSpecsIcon from '../icons/shared/support/TechnicalSpecsIcon'
import DesignTabIcon from '../icons/shared/support/DesignTabIcon'
import {getImageForProductTile} from '../../utils/utils'
import {getPriceData} from '../../utils/product-utils'
import {getProductUrl} from '../../hooks/use-product-data'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useHexColor from '../../hooks/use-hex-color'
import DynamicIcon from '../dynamic-icon/DynamicIcon'
import {getTechSpecIconId} from '../../utils/dynamicIconHelper'
import {remCalc} from '../../theme/_utils'

const MISSING_SPEC_VALUE = '-'
const FALLBACK_FUNCTIONS_TAB = 'Functions'

// matches the static right-side bleed baked into styles.productSpecsContainer
const OVERFLOW_BLEED = remCalc(12)

// once scrolled off the start, cancel the left inset the same way the right edge already bleeds
const getHorizontalBleedStyle = (overflowsLeft) => ({
    marginLeft: {base: overflowsLeft ? `-${OVERFLOW_BLEED}` : 0, lg: 0},
    width: {
        base: overflowsLeft
            ? `calc(100% + ${OVERFLOW_BLEED} + ${OVERFLOW_BLEED})`
            : `calc(100% + ${OVERFLOW_BLEED})`,
        md: '100%',
    },
})

const isFunctionsCategory = (value) => {
    const normalized = String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    return normalized.includes('function') || normalized.includes('funzion')
}

const getCompareSlotKey = (idx) => `compare-slot-${idx}`

const hideScrollbarSx = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
        width: 0,
        height: 0,
        display: 'none',
    },
}

const toCompareImage450 = (url) => {
    if (!url || typeof url !== 'string') return url
    if (!url.includes('dam.delonghi.com')) return url

    if (/\/\d+x\d+\//.test(url)) {
        return url.replace(/\/\d+x\d+\//, '/450x450/')
    }

    if (url.includes('/assets/')) {
        return url.replace('/assets/', '/450x450/assets/')
    }

    return url
}

const buildSelectedVariantMap = ({
    productsWithVariationGroups,
    recommendedVariantBySlot,
    pdpCurrentProductId,
    selectionOverrides,
}) => {
    const next = {}
    const usedVariantIds = new Set()

    productsWithVariationGroups.forEach((prod, idx) => {
        const key = getCompareSlotKey(idx)
        const isMainProductSlot = idx === 0
        const currentPdpVariantId = prod?.c_subModelIdentifier || prod?.productId
        const groupVariantIds = (prod?.c_enabledVariationGroups || [])
            .map((variant) => variant?.productId)
            .filter(Boolean)
        const fallbackVariantIds = [prod?.c_subModelIdentifier, prod?.productId, prod?.id].filter(
            Boolean,
        )
        const availableVariantIds =
            groupVariantIds.length > 0 ? groupVariantIds : fallbackVariantIds
        const recommendedVariantId = recommendedVariantBySlot[key]

        const canUseVariantId = (variantId) => {
            if (!variantId) return false
            if (!availableVariantIds.includes(variantId)) return false
            if (usedVariantIds.has(variantId)) return false
            if (!isMainProductSlot && variantId === pdpCurrentProductId) return false
            return true
        }

        const candidateVariantIds = [
            selectionOverrides[key],
            isMainProductSlot ? currentPdpVariantId : null,
            recommendedVariantId,
            ...availableVariantIds,
        ].filter(Boolean)

        const selectedVariantId =
            candidateVariantIds.find((variantId) => canUseVariantId(variantId)) ||
            availableVariantIds.find((variantId) => {
                if (!variantId) return false
                if (!isMainProductSlot && variantId === pdpCurrentProductId) return false
                return true
            }) ||
            null

        next[key] = selectedVariantId
        if (selectedVariantId) {
            usedVariantIds.add(selectedVariantId)
        }
    })

    return next
}

const ProductCompare = ({product, initialProducts = []}) => {
    const [viewMode, setViewMode] = useState('cards')
    const [selectedSpecCategory, setSelectedSpecCategory] = useState(null)
    const [selectedVariantOverrides, setSelectedVariantOverrides] = useState({})
    const [cardsOverflowLeft, setCardsOverflowLeft] = useState(false)
    const [specsOverflowLeft, setSpecsOverflowLeft] = useState(false)

    const handleHorizontalScroll = (setOverflowsLeft) => (event) => {
        setOverflowsLeft(event.currentTarget.scrollLeft > 1)
    }
    const styles = useStyleConfig('ProductCompare')
    const {currency} = useCurrency()
    const navigate = useNavigation()
    const {resolveHexColor, getHexFromObject} = useHexColor()

    const handleCategoryClick = (category) => {
        setSelectedSpecCategory(category)
        setViewMode('specs')
    }

    const activateOnEnterSpace = (event, callback) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            callback()
        }
    }

    const sanitizeIdPart = (value) =>
        String(value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
    const getSpecTabId = (category) => `product-compare-tab-${sanitizeIdPart(category)}`
    const getSpecPanelId = (category) => `product-compare-panel-${sanitizeIdPart(category)}`

    const getTabId = (tabKey) =>
        tabKey === 'cards' ? 'product-compare-tab-cards' : getSpecTabId(tabKey)

    const activateTab = (tabKey) => {
        if (tabKey === 'cards') {
            setViewMode('cards')
            return
        }

        handleCategoryClick(tabKey)
    }

    const focusTab = (tabKey) => {
        if (typeof document === 'undefined') return
        const tabElement = document.getElementById(getTabId(tabKey))
        tabElement?.focus()
    }

    const handleVariantSelect = (productKey, variantId) => {
        if (!variantId) return

        setSelectedVariantOverrides((prev) => ({
            ...prev,
            [productKey]: variantId,
        }))
    }

    const pdpCurrentProductId = product?.c_subModelIdentifier || product?.productId || product?.id
    const currentProductIds = useMemo(
        () =>
            new Set(
                [
                    product?.id,
                    product?.productId,
                    product?.c_subModelIdentifier,
                    pdpCurrentProductId,
                ].filter(Boolean),
            ),
        [product, pdpCurrentProductId],
    )

    const compareRecommendations = useMemo(() => {
        const seenRecommendationKeys = new Set()

        return (product?.c_recommendations || [])
            .filter((rec) => rec?.recommendationType?.value === 3)
            .filter((rec) => {
                const recommendationKey = rec?.masterProductId || rec?.recommendedItemId
                if (!recommendationKey) return false

                if (
                    currentProductIds.has(rec?.masterProductId) ||
                    currentProductIds.has(rec?.recommendedItemId)
                ) {
                    return false
                }

                if (seenRecommendationKeys.has(recommendationKey)) {
                    return false
                }

                seenRecommendationKeys.add(recommendationKey)
                return true
            })
            .slice(0, 2)
    }, [product, currentProductIds])
    const hasCompareRecommendations = compareRecommendations.length > 0

    const comparableMasterProductsIds = compareRecommendations
        .map((rec) => rec?.masterProductId)
        .filter(Boolean)

    const comparableRecommendedVariantIds = compareRecommendations
        .map((rec) => rec?.recommendedItemId)
        .filter(Boolean)

    const recommendedVariantBySlot = useMemo(() => {
        const bySlot = {}

        compareRecommendations.forEach((rec, idx) => {
            bySlot[getCompareSlotKey(idx + 1)] = rec?.recommendedItemId || null
        })

        return bySlot
    }, [compareRecommendations])

    const {data: comparedMasterProducts, isLoading: isLoadingMasterProducts} = useProducts(
        {
            parameters: {
                ids: comparableMasterProductsIds.join(','),
                expand: ['availability', 'images'],
                c_includeTechSpecs: true,
            },
        },
        {
            keepPreviousData: true,
            enabled: comparableMasterProductsIds.length > 0 && initialProducts.length === 0,
        },
    )

    const normalizedComparedMasterProducts =
        initialProducts.length > 0
            ? initialProducts.filter((item) => comparableMasterProductsIds.includes(item?.id))
            : Array.isArray(comparedMasterProducts)
            ? comparedMasterProducts
            : comparedMasterProducts?.data || []

    const {data: comparedVariantProducts, isLoading: isLoadingVariantProducts} = useProducts(
        {
            parameters: {
                ids: comparableRecommendedVariantIds.join(','),
                expand: ['availability', 'images'],
                c_includeTechSpecs: true,
            },
        },
        {
            keepPreviousData: true,
            enabled: comparableRecommendedVariantIds.length > 0 && initialProducts.length === 0,
        },
    )

    const normalizedComparedVariantProducts =
        initialProducts.length > 0
            ? initialProducts.filter((item) => comparableRecommendedVariantIds.includes(item?.id))
            : Array.isArray(comparedVariantProducts)
            ? comparedVariantProducts
            : comparedVariantProducts?.data || []

    const updatedComparedProducts = useMemo(() => {
        const masterById = new Map(normalizedComparedMasterProducts.map((item) => [item.id, item]))
        const variantById = new Map(
            normalizedComparedVariantProducts.map((item) => [item.id, item]),
        )

        const uniqueComparedProducts = []
        const seenComparedProductKeys = new Set()

        compareRecommendations.forEach((rec) => {
            let resolvedProduct = null

            if (rec?.masterProductId) {
                resolvedProduct = masterById.get(rec.masterProductId)
            }

            if (!resolvedProduct && rec?.recommendedItemId) {
                resolvedProduct = variantById.get(rec.recommendedItemId)
            }

            if (!resolvedProduct) return

            const resolvedIdentifiers = [
                resolvedProduct?.id,
                resolvedProduct?.productId,
                resolvedProduct?.c_subModelIdentifier,
            ].filter(Boolean)

            if (resolvedIdentifiers.some((identifier) => currentProductIds.has(identifier))) {
                return
            }

            const resolvedProductKey = resolvedIdentifiers[0]
            if (!resolvedProductKey || seenComparedProductKeys.has(resolvedProductKey)) {
                return
            }

            seenComparedProductKeys.add(resolvedProductKey)
            uniqueComparedProducts.push(resolvedProduct)
        })

        return uniqueComparedProducts.slice(0, 2)
    }, [
        compareRecommendations,
        currentProductIds,
        normalizedComparedMasterProducts,
        normalizedComparedVariantProducts,
    ])

    const baseProductsForCompare = hasCompareRecommendations
        ? [product, ...updatedComparedProducts]
        : []

    const getVariationGroups = (prod) => {
        const enabledGroups = prod?.c_enabledVariationGroups || []
        if (enabledGroups.length > 0) return enabledGroups

        const rawVariants = prod?.c_variants || prod?.variants || []
        return rawVariants
            .map((variant) => {
                const productId =
                    variant?.productId ||
                    variant?.id ||
                    variant?.variationValues?.subModelIdentifier
                if (!productId) return null

                return {
                    ...variant,
                    productId,
                    subModelIdentifier: variant?.variationValues?.subModelIdentifier || productId,
                    swatch: variant?.swatch || null,
                    hexColor: variant?.c_hexColor || variant?.hexColor || null,
                }
            })
            .filter(Boolean)
    }

    const productsWithVariationGroups = useMemo(
        () =>
            baseProductsForCompare.map((prod) => ({
                ...prod,
                c_enabledVariationGroups: getVariationGroups(prod),
            })),
        [baseProductsForCompare],
    )

    const selectedVariantByProduct = useMemo(
        () =>
            buildSelectedVariantMap({
                productsWithVariationGroups,
                recommendedVariantBySlot,
                pdpCurrentProductId,
                selectionOverrides: selectedVariantOverrides,
            }),
        [
            productsWithVariationGroups,
            recommendedVariantBySlot,
            pdpCurrentProductId,
            selectedVariantOverrides,
        ],
    )

    const isCurrentPdpSelection = (prod, idx) => {
        const slotKey = getCompareSlotKey(idx)
        const selectedId = selectedVariantByProduct[slotKey]
        const effectiveProductId =
            selectedId || prod?.c_subModelIdentifier || prod?.productId || prod?.id

        return Boolean(
            pdpCurrentProductId && effectiveProductId && effectiveProductId === pdpCurrentProductId,
        )
    }

    const allVariantIds = useMemo(() => {
        if (!hasCompareRecommendations) return []

        const selectedIds = productsWithVariationGroups
            .map((prod, idx) => {
                const key = getCompareSlotKey(idx)
                return (
                    selectedVariantByProduct[key] || prod?.c_enabledVariationGroups?.[0]?.productId
                )
            })
            .filter(Boolean)

        return Array.from(new Set(selectedIds))
    }, [hasCompareRecommendations, productsWithVariationGroups, selectedVariantByProduct])

    const {data: variantsData} = useProducts(
        {
            parameters: {
                ids: allVariantIds.join(','),
                expand: ['availability', 'images'],
                c_includeTechSpecs: true,
            },
        },
        {
            keepPreviousData: true,
            enabled: allVariantIds.length > 0,
        },
    )

    const normalizedVariants = Array.isArray(variantsData) ? variantsData : variantsData?.data || []
    const variantsById = useMemo(
        () => new Map(normalizedVariants.map((item) => [item.id, item])),
        [normalizedVariants],
    )

    const resolvedProducts = productsWithVariationGroups.map((prod, idx) => {
        const key = getCompareSlotKey(idx)
        const selectedVariantId = selectedVariantByProduct[key]
        const selectedVariant = selectedVariantId ? variantsById.get(selectedVariantId) : null

        if (!selectedVariant) {
            return prod
        }

        return {
            ...prod,
            ...selectedVariant,
            c_enabledVariationGroups: prod?.c_enabledVariationGroups || [],
            specifications:
                selectedVariant?.specifications ||
                selectedVariant?.c_specifications ||
                prod?.specifications ||
                prod?.c_specifications ||
                [],
            c_specifications:
                selectedVariant?.c_specifications ||
                selectedVariant?.specifications ||
                prod?.c_specifications ||
                prod?.specifications ||
                [],
        }
    })

    const mainProductSpecifications = useSpecifications(
        hasCompareRecommendations ? resolvedProducts[0] || product : null,
    )
    const allProducts = hasCompareRecommendations
        ? resolvedProducts.map((prod, idx) =>
              idx === 0 ? {...prod, specifications: mainProductSpecifications} : prod,
          )
        : []
    const allProductSpecifications = useArrayOfSpecifications(allProducts)

    const hasComparedProducts = updatedComparedProducts.length > 0

    const specCategories = useMemo(() => {
        const categories = new Set()

        allProductSpecifications.forEach((specs) => {
            ;(Array.isArray(specs) ? specs : []).forEach((item) => {
                categories.add(item.accordion_title)
            })
        })

        return Array.from(categories)
    }, [allProductSpecifications])

    const displaySpecCategories = useMemo(() => {
        const categories = [...specCategories]
        const hasFunctionsTab = categories.some((category) => isFunctionsCategory(category))

        if (!hasFunctionsTab) {
            categories.push(FALLBACK_FUNCTIONS_TAB)
        }

        return categories
    }, [specCategories])

    const effectiveSelectedSpecCategory = useMemo(() => {
        if (displaySpecCategories.length === 0) return null

        return selectedSpecCategory && displaySpecCategories.includes(selectedSpecCategory)
            ? selectedSpecCategory
            : displaySpecCategories[0]
    }, [selectedSpecCategory, displaySpecCategories])

    const tabOrder = useMemo(() => ['cards', ...displaySpecCategories], [displaySpecCategories])

    const handleTabKeyNavigation = (event, currentTabKey) => {
        const currentIndex = tabOrder.indexOf(currentTabKey)
        if (currentIndex < 0) return

        switch (event.key) {
            case 'ArrowLeft': {
                event.preventDefault()
                const targetIndex =
                    currentIndex === 0 ? tabOrder.length - 1 : Math.max(currentIndex - 1, 0)
                focusTab(tabOrder[targetIndex])
                break
            }
            case 'ArrowRight': {
                event.preventDefault()
                const targetIndex =
                    currentIndex === tabOrder.length - 1
                        ? 0
                        : Math.min(currentIndex + 1, tabOrder.length - 1)
                focusTab(tabOrder[targetIndex])
                break
            }
            case 'Home': {
                event.preventDefault()
                focusTab(tabOrder[0])
                break
            }
            case 'End': {
                event.preventDefault()
                focusTab(tabOrder[tabOrder.length - 1])
                break
            }
            case 'Enter':
            case ' ': {
                activateOnEnterSpace(event, () => activateTab(currentTabKey))
                break
            }
            default:
                break
        }
    }

    const allValueNames = useMemo(() => {
        if (viewMode !== 'specs') return []

        const values = new Set()

        allProductSpecifications.forEach((specs) => {
            ;(Array.isArray(specs) ? specs : []).forEach((item) => {
                item.values?.forEach?.((v) => {
                    values.add(v.value_name)
                })
            })
        })

        return Array.from(values)
    }, [allProductSpecifications, viewMode])

    const specsWithCategory = useMemo(() => {
        if (viewMode !== 'specs') return new Map()

        const byCategory = new Map()

        displaySpecCategories.forEach((category) => {
            const categorySpecs = []

            allValueNames.forEach((valueName) => {
                let belongsToCategory = false
                const values = allProducts.map((prod, idx) => {
                    const productSpecs = allProductSpecifications[idx] || []
                    const valueObj = (Array.isArray(productSpecs) ? productSpecs : [])
                        .find((item) => item.accordion_title === category)
                        ?.values?.find((v) => v.value_name === valueName)

                    if (valueObj) belongsToCategory = true
                    return valueObj?.value
                        ? String(valueObj.value).split(',').join(', ')
                        : MISSING_SPEC_VALUE
                })

                if (belongsToCategory) {
                    categorySpecs.push({
                        id: valueName,
                        label: valueName,
                        values,
                    })
                }
            })

            byCategory.set(category, categorySpecs)
        })

        return byCategory
    }, [viewMode, displaySpecCategories, allValueNames, allProducts, allProductSpecifications])

    const filteredSpecs = useMemo(
        () => specsWithCategory.get(effectiveSelectedSpecCategory) || [],
        [specsWithCategory, effectiveSelectedSpecCategory],
    )

    if (!hasComparedProducts) {
        const isLoadingComparedProducts = isLoadingMasterProducts || isLoadingVariantProducts

        if (hasCompareRecommendations && isLoadingComparedProducts) {
            return (
                <Flex
                    data-testid="product-compare-section-loading"
                    {...styles.productCompareContainer}
                >
                    <Flex {...styles.headerContainer}>
                        <Text {...styles.headerText}>
                            <FormattedMessage
                                defaultMessage="Compare products"
                                id="product-compare.header"
                            />
                        </Text>
                    </Flex>
                    <Stack spacing={4} width="100%">
                        <Skeleton height={remCalc(56)} borderRadius="2xl" />
                        <Skeleton height={remCalc(160)} borderRadius="2xl" />
                        <Skeleton height={remCalc(96)} borderRadius="2xl" />
                    </Stack>
                </Flex>
            )
        }

        return null
    }

    return (
        <Flex data-testid="product-compare-section" {...styles.productCompareContainer}>
            <Flex {...styles.headerContainer}>
                <Text {...styles.headerText}>
                    <FormattedMessage
                        defaultMessage="Compare products"
                        id="product-compare.header"
                    />
                </Text>
            </Flex>
            <Flex {...styles.radioGroupContainer} sx={hideScrollbarSx}>
                <Stack
                    role="tablist"
                    aria-label="Product comparison tabs"
                    {...styles.radioGroupShell}
                    sx={hideScrollbarSx}
                >
                    <Flex
                        as="label"
                        role="tab"
                        id="product-compare-tab-cards"
                        aria-controls="product-compare-panel-cards"
                        aria-selected={viewMode === 'cards'}
                        tabIndex={viewMode === 'cards' ? 0 : -1}
                        {...styles.radioOption}
                        {...(viewMode === 'cards' ? styles.radioOptionActive : {})}
                        {...styles.radioOptionFirst}
                        onClick={() => setViewMode('cards')}
                        onKeyDown={(event) => handleTabKeyNavigation(event, 'cards')}
                        cursor="pointer"
                    >
                        <Radio
                            isChecked={viewMode === 'cards'}
                            onChange={() => setViewMode('cards')}
                            srOnly
                        />
                        <DesignTabIcon {...styles.tabIcon} aria-hidden="true" />
                        <Text>
                            <FormattedMessage
                                defaultMessage="Design"
                                id="product-compare.radio.design"
                            />
                        </Text>
                    </Flex>

                    {displaySpecCategories.map((category, index) => {
                        const groupedTechSpecs =
                            allProducts?.[0]?.c_groupedTechSpecs || product?.c_groupedTechSpecs
                        const categoryIconId = getTechSpecIconId({
                            groupedTechSpecs,
                            category,
                            index,
                            categories: displaySpecCategories,
                            sourceKey: Object.keys(groupedTechSpecs || {})[0],
                        })

                        return (
                            <Flex
                                key={category}
                                as="label"
                                role="tab"
                                id={getSpecTabId(category)}
                                aria-controls={getSpecPanelId(category)}
                                aria-selected={
                                    viewMode === 'specs' &&
                                    effectiveSelectedSpecCategory === category
                                }
                                tabIndex={
                                    viewMode === 'specs' &&
                                    effectiveSelectedSpecCategory === category
                                        ? 0
                                        : -1
                                }
                                {...styles.specCategoryOption}
                                {...(viewMode === 'specs' &&
                                effectiveSelectedSpecCategory === category
                                    ? styles.specCategoryOptionActive
                                    : {})}
                                onClick={() => handleCategoryClick(category)}
                                onKeyDown={(event) => handleTabKeyNavigation(event, category)}
                                cursor="pointer"
                            >
                                <Radio
                                    isChecked={
                                        viewMode === 'specs' &&
                                        effectiveSelectedSpecCategory === category
                                    }
                                    onChange={() => handleCategoryClick(category)}
                                    srOnly
                                />
                                <DynamicIcon
                                    iconId={categoryIconId}
                                    {...styles.tabIcon}
                                    aria-hidden="true"
                                />
                                <Text>{category}</Text>
                            </Flex>
                        )
                    })}
                </Stack>
            </Flex>

            {viewMode === 'cards' && (
                <Flex
                    role="tabpanel"
                    id="product-compare-panel-cards"
                    aria-labelledby="product-compare-tab-cards"
                    {...styles.productSpecsContainer}
                    onScroll={handleHorizontalScroll(setCardsOverflowLeft)}
                    {...getHorizontalBleedStyle(cardsOverflowLeft)}
                    sx={hideScrollbarSx}
                >
                    {allProducts.map((prod, idx) => {
                        const productKey = getCompareSlotKey(idx)
                        const variationGroups = prod?.c_enabledVariationGroups || []
                        const priceData = getPriceData(prod)
                        const productUrl = getProductUrl(prod)
                        const isCurrentProductSelection = isCurrentPdpSelection(prod, idx)

                        return (
                            <Flex key={idx} {...styles.productCard}>
                                <Flex
                                    {...styles.productCardInfoSection}
                                    order={{base: 2, sm: 2, md: 0, lg: 0}}
                                >
                                    <Flex {...styles.titlePriceGroup}>
                                        <Text as={'h2'} {...styles.productCardName}>
                                            <Link
                                                to={productUrl}
                                                aria-label={`See details - ${
                                                    prod?.c_defaultName || `Product ${idx + 1}`
                                                }`}
                                            >
                                                {prod?.c_defaultName}
                                            </Link>
                                        </Text>
                                        <Flex {...styles.priceDisplayWrapper}>
                                            <DisplayPrice
                                                labelForA11y={
                                                    prod?.c_defaultName || `Product ${idx + 1}`
                                                }
                                                priceData={priceData}
                                                currency={prod?.currency || currency}
                                                productDetailPriceView={true}
                                                productDetailPriceAs="p"
                                                productDetailCurrentPriceTextStyle="body-small"
                                                productDetailListPriceTextStyle="body-small"
                                                priceContainerProps={
                                                    styles.productCardPriceContainer
                                                }
                                                currentPriceProps={styles.productCardPrice}
                                                listPriceProps={styles.listPrice}
                                                discountProps={{
                                                    textStyle: 'body-small',
                                                    display: {
                                                        base: 'none',
                                                        sm: 'none',
                                                        md: 'block',
                                                        lg: 'block',
                                                    },
                                                }}
                                            />
                                            <Box {...styles.omnibusPriceSlot}>
                                                <OmnibusPrice
                                                    priceData={priceData}
                                                    currency={prod?.currency || currency}
                                                    omnibusExpandedView={false}
                                                    variant="compare"
                                                />
                                            </Box>
                                        </Flex>
                                    </Flex>
                                    <Flex {...styles.productCardActionContainer}>
                                        {isCurrentProductSelection ? (
                                            <Flex as="span" {...styles.productCardCurrentBadge}>
                                                <VisuallyHidden>Current product</VisuallyHidden>
                                                <FormattedMessage
                                                    defaultMessage="Current"
                                                    id="product-compare.card.current"
                                                />
                                            </Flex>
                                        ) : (
                                            <Button
                                                type="button"
                                                onClick={() => navigate(productUrl)}
                                                aria-label={`See details - ${
                                                    prod?.c_defaultName || `Product ${idx + 1}`
                                                }`}
                                                rightIcon={
                                                    <ChevronRightIcon
                                                        {...styles.productCardDetailsIcon}
                                                    />
                                                }
                                                variant={'link-button'}
                                                size={'base'}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="See details"
                                                    id="product-compare.card.see_details"
                                                />
                                            </Button>
                                        )}
                                    </Flex>
                                </Flex>
                                <Flex
                                    {...styles.cardContentContainer}
                                    order={{base: 1, sm: 1, md: 0, lg: 0}}
                                >
                                    <AspectRatio {...styles.productCardImageContainer}>
                                        <Link
                                            to={productUrl}
                                            aria-label={`See details - ${
                                                prod?.c_defaultName || `Product ${idx + 1}`
                                            }`}
                                        >
                                            <Box {...styles.productCardImageBox}>
                                                <DynamicImage
                                                    src={toCompareImage450(
                                                        getImageForProductTile(prod),
                                                    )}
                                                    imageProps={{
                                                        alt: prod?.c_defaultName || '',
                                                        loading: 'lazy',
                                                    }}
                                                />
                                            </Box>
                                        </Link>
                                    </AspectRatio>
                                    {variationGroups.length > 0 && (
                                        <Box {...styles.cardSwatchContainer}>
                                            <SwatchGroup
                                                ariaLabel={`${
                                                    prod?.c_defaultName || 'Product'
                                                } variants`}
                                                label=""
                                                value={selectedVariantByProduct[productKey]}
                                                handleChange={(value) =>
                                                    handleVariantSelect(productKey, value)
                                                }
                                            >
                                                {variationGroups.map((variant, index) => {
                                                    const variantId =
                                                        variant?.productId ||
                                                        variant?.subModelIdentifier
                                                    const hexColor = resolveHexColor(
                                                        getHexFromObject(variant?.swatch),
                                                        getHexFromObject(variant),
                                                        variant?.c_hexColor,
                                                        variant?.c_hexColour,
                                                        variant?.hexColor,
                                                    )

                                                    return (
                                                        <Swatch
                                                            key={variantId}
                                                            value={variantId}
                                                            name={
                                                                variant?.colorName ||
                                                                variant?.c_color ||
                                                                variant?.name ||
                                                                variant?.subModelIdentifier ||
                                                                variantId
                                                            }
                                                            variant="square"
                                                            size="compare"
                                                            position={index + 1}
                                                            total={variationGroups.length}
                                                            hexColor={hexColor}
                                                            clickOnly
                                                        />
                                                    )
                                                })}
                                            </SwatchGroup>
                                        </Box>
                                    )}
                                </Flex>
                            </Flex>
                        )
                    })}
                </Flex>
            )}

            {viewMode === 'specs' && (
                <Flex
                    role="tabpanel"
                    id={getSpecPanelId(effectiveSelectedSpecCategory)}
                    aria-labelledby={getSpecTabId(effectiveSelectedSpecCategory)}
                    {...styles.productSpecsContainer}
                    {...styles.specsTabPanel}
                    onScroll={handleHorizontalScroll(setSpecsOverflowLeft)}
                    {...getHorizontalBleedStyle(specsOverflowLeft)}
                    sx={hideScrollbarSx}
                >
                    <Flex
                        {...styles.productSpecsContainer}
                        {...styles.specsRow}
                        sx={{
                            gridTemplateColumns: {
                                base: `repeat(${allProducts.length}, ${remCalc(170)})`,
                                sm: `repeat(${allProducts.length}, ${remCalc(170)})`,
                                md: `repeat(${allProducts.length}, 1fr)`,
                                lg: `repeat(${allProducts.length}, 1fr)`,
                            },
                        }}
                    >
                        {allProducts.map((prod, idx) => {
                            const priceData = getPriceData(prod)
                            const productUrl = getProductUrl(prod)
                            const isCurrentProductSelection = isCurrentPdpSelection(prod, idx)

                            return (
                                <Flex key={idx} {...styles.productCard} {...styles.specsHeaderCard}>
                                    <Link
                                        to={productUrl}
                                        aria-label={`See details - ${
                                            prod?.c_defaultName || `Product ${idx + 1}`
                                        }`}
                                    >
                                        <Box {...styles.specsCardImage}>
                                            <DynamicImage
                                                src={toCompareImage450(
                                                    getImageForProductTile(prod),
                                                )}
                                                imageProps={{
                                                    alt: prod?.c_defaultName || '',
                                                    loading: 'lazy',
                                                }}
                                            />
                                        </Box>
                                    </Link>
                                    <Text
                                        as={'h2'}
                                        {...styles.productCardName}
                                        {...styles.specsProductCardName}
                                    >
                                        <Link
                                            to={productUrl}
                                            aria-label={`See details - ${
                                                prod?.c_defaultName || `Product ${idx + 1}`
                                            }`}
                                        >
                                            {prod?.c_defaultName}
                                        </Link>
                                    </Text>
                                    <Flex {...styles.specsPricingAndActionColumn}>
                                        <Flex {...styles.priceDisplayWrapper}>
                                            <DisplayPrice
                                                labelForA11y={
                                                    prod?.c_defaultName || `Product ${idx + 1}`
                                                }
                                                priceData={priceData}
                                                currency={prod?.currency || currency}
                                                productDetailPriceView={true}
                                                productDetailPriceAs="p"
                                                productDetailCurrentPriceTextStyle="body-small"
                                                productDetailListPriceTextStyle="body-small"
                                                priceContainerProps={
                                                    styles.productCardPriceContainer
                                                }
                                                currentPriceProps={styles.productCardPrice}
                                                listPriceProps={styles.listPrice}
                                                discountProps={{
                                                    textStyle: 'body-small',
                                                    display: {
                                                        base: 'none',
                                                        sm: 'none',
                                                        md: 'block',
                                                        lg: 'block',
                                                    },
                                                }}
                                            />
                                            <Box {...styles.omnibusPriceSlot}>
                                                <OmnibusPrice
                                                    priceData={priceData}
                                                    currency={prod?.currency || currency}
                                                    omnibusExpandedView={false}
                                                    variant="compare"
                                                />
                                            </Box>
                                        </Flex>
                                        <Flex {...styles.productCardActionContainer}>
                                            {isCurrentProductSelection ? (
                                                <Flex as="span" {...styles.productCardCurrentBadge}>
                                                    <VisuallyHidden>Current product</VisuallyHidden>
                                                    <Text>
                                                        <FormattedMessage
                                                            defaultMessage="Current"
                                                            id="product-compare.card.current"
                                                        />
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="link-button"
                                                    size="base"
                                                    as={Link}
                                                    to={productUrl}
                                                    aria-label={`See details - ${
                                                        prod?.c_defaultName || `Product ${idx + 1}`
                                                    }`}
                                                    rightIcon={
                                                        <ChevronRightIcon
                                                            {...styles.productCardDetailsIcon}
                                                        />
                                                    }
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="See details"
                                                        id="product-compare.card.see_details"
                                                    />
                                                </Button>
                                            )}
                                        </Flex>
                                    </Flex>
                                </Flex>
                            )
                        })}
                    </Flex>
                    <Flex
                        {...styles.specsListContainer}
                        sx={{
                            gridTemplateColumns: {
                                base: `repeat(${allProducts.length}, ${remCalc(170)})`,
                                sm: `repeat(${allProducts.length}, ${remCalc(170)})`,
                                md: `repeat(${allProducts.length}, 1fr)`,
                                lg: `repeat(${allProducts.length}, 1fr)`,
                            },
                        }}
                    >
                        {filteredSpecs.map((spec, specIndex) =>
                            allProducts.map((prod, idx) => (
                                <Flex
                                    key={`${spec.id}-${idx}`}
                                    {...styles.specItem}
                                    {...(specIndex === filteredSpecs.length - 1
                                        ? {borderBottom: 'none'}
                                        : {})}
                                >
                                    <Flex {...styles.specTitleGroup}>
                                        <TechnicalSpecsIcon
                                            {...styles.specTitleIcon}
                                            sx={{
                                                'path:last-of-type': {
                                                    stroke: 'currentColor',
                                                },
                                            }}
                                            aria-hidden="true"
                                        />
                                        <Text {...styles.specLabel}>{spec.label}</Text>
                                    </Flex>
                                    <Text {...styles.specValue}>
                                        {spec.values[idx] || MISSING_SPEC_VALUE}
                                    </Text>
                                </Flex>
                            )),
                        )}
                    </Flex>
                </Flex>
            )}
        </Flex>
    )
}

ProductCompare.propTypes = {
    product: PropTypes.object,
    initialProducts: PropTypes.array,
}

export default ProductCompare
